import { useState } from 'react';
import styled from 'styled-components';
import { useAssistGeoStampMutation } from '@shared/api';
import {
  STAMP_FIELDS,
  stampFieldLabel,
  type GeoStampAssist,
  type GeoStampField,
  type GeoStampSuggestion,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { AiBadge, Hint, Muted, SourceTag, useAction } from '@widgets/GeoSchemes';
import { useWizard } from './wizard';

/** Запрос предложений по листу РД: ничего не сохраняет, только возвращает список. */
export function useStampAssist() {
  const { scheme } = useWizard();
  const [ask] = useAssistGeoStampMutation();
  const action = useAction();
  const [result, setResult] = useState<GeoStampAssist | null>(null);
  const run = async () => {
    const res = await action.runValue(
      () => ask(scheme.id).unwrap(),
      'Реквизиты по листу РД не получены.',
    );
    if (res) setResult(res);
  };
  return { result, run, busy: action.busy, error: action.error, clear: () => setResult(null) };
}

/* Без overflow: hidden — подсказку бейджа «ИИ-помощник» не обрежет. */
const Box = styled.section`
  container: geo-stamp-ai / inline-size;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => `${theme.radius.md} ${theme.radius.md} 0 0`};
  background: ${({ theme }) => theme.colors.surface};
`;

const Title = styled.h4`
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'label action'
    'value action'
    'meta action';
  column-gap: ${({ theme }) => theme.spacing(4)};
  row-gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }

  @container geo-stamp-ai (max-width: 440px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'label' 'value' 'meta' 'action';
    row-gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;

const ItemLabel = styled.span`
  grid-area: label;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ItemValue = styled.span`
  grid-area: value;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const ItemMeta = styled.span`
  grid-area: meta;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const ItemAction = styled.span`
  grid-area: action;
  justify-self: end;

  @container geo-stamp-ai (max-width: 440px) {
    justify-self: start;
  }
`;

const Footer = styled.div`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};

  ul + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const norm = (s: string) => s.trim().replace(/\s+/g, ' ');

const FIELD_ORDER = new Map(STAMP_FIELDS.map((f, i) => [f.field, i]));

/** По порядку граф формы; внутри графы — сначала штамп РД (его прочитала программа). */
function sortSuggestions(list: GeoStampSuggestion[]): GeoStampSuggestion[] {
  return list
    .slice()
    .sort(
      (a, b) =>
        (FIELD_ORDER.get(a.field) ?? 99) - (FIELD_ORDER.get(b.field) ?? 99) ||
        (a.source === b.source ? 0 : a.source === 'rd_stamp' ? -1 : 1),
    );
}

/**
 * Предложения реквизитов штампа по листу РД. Каждое — с источником: «штамп РД»
 * (прочитала программа) или «ИИ-помощник · найдено в тексте листа». Ничего не
 * применяется само: «Применить» у строки или «Применить все».
 */
export function StampSuggestions({
  result,
  current,
  busy,
  onApply,
  onClose,
}: {
  result: GeoStampAssist;
  /** Текущие значения граф в черновике шага — для сравнения. */
  current: Readonly<Record<GeoStampField, string>>;
  busy: boolean;
  onApply: (items: GeoStampSuggestion[]) => void;
  onClose: () => void;
}) {
  const list = sortSuggestions(result.suggestions);
  const same = (s: GeoStampSuggestion) => norm(current[s.field]) === norm(s.value);
  // «Применить все»: по одному значению на графу (предпочтительно из штампа РД),
  // графы, где уже так, не трогаем.
  const preferred = new Map<GeoStampField, GeoStampSuggestion>();
  for (const s of list) if (!preferred.has(s.field)) preferred.set(s.field, s);
  const toApply = [...preferred.values()].filter((s) => !same(s));

  return (
    <Box aria-labelledby="geo-stamp-ai-title">
      <Head>
        <Title id="geo-stamp-ai-title">Предложения по листу РД · {list.length}</Title>
        <Actions>
          {list.length > 0 && (
            <Button
              type="button"
              $variant="primary"
              $size="sm"
              onClick={() => onApply(toApply)}
              disabled={busy || toApply.length === 0}
            >
              Применить все
            </Button>
          )}
          <Button type="button" $variant="ghost" $size="sm" onClick={onClose} disabled={busy}>
            Скрыть
          </Button>
        </Actions>
      </Head>
      {list.length === 0 ? (
        <Footer>
          <Muted>{result.note ?? 'На листе не нашлось реквизитов для основной надписи.'}</Muted>
        </Footer>
      ) : (
        <>
          <List>
            {list.map((s, i) => {
              const label = stampFieldLabel(s.field);
              const isSame = same(s);
              const now = norm(current[s.field]);
              return (
                <Item key={`${s.field}-${s.source}-${i}`}>
                  <ItemLabel>{label}</ItemLabel>
                  <ItemValue>{s.value}</ItemValue>
                  <ItemMeta>
                    {s.source === 'llm' ? (
                      <AiBadge detail="найдено в тексте листа" />
                    ) : (
                      <SourceTag>штамп РД</SourceTag>
                    )}
                    {!isSame && <span>· {now ? `сейчас: «${now}»` : 'графа пустая'}</span>}
                  </ItemMeta>
                  <ItemAction>
                    <Button
                      type="button"
                      $variant="secondary"
                      $size="sm"
                      onClick={() => onApply([s])}
                      disabled={busy || isSame}
                      aria-label={isSame ? `${label}: совпадает` : `Применить: ${label}`}
                    >
                      {isSame ? 'Совпадает' : 'Применить'}
                    </Button>
                  </ItemAction>
                </Item>
              );
            })}
          </List>
          {result.note && (
            <Footer>
              <Hint>{result.note}</Hint>
            </Footer>
          )}
        </>
      )}
    </Box>
  );
}
